import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { productFeature } from 'lib/utils/productFeature';
import {
  getAdminAccessList,
  SuperAdminRole,
  ALLOWED_CUSTOMERS,
  checkTemporaryShowLandingPageBuilderExceptions,
} from 'lib/const/SuperAdminRole';
import {
  CAMPING_WORLD_IDS,
  ADMIN_CUSTOMER_IDS,
  DEFAULT_RESELLER_ID,
  SCREEN_VALUES,
} from 'lib/const/SuperAdminConstants';
import {
  MdAddLink,
  MdLocationPin,
  MdLaunch,
  MdSupervisorAccount,
  MdContacts,
  MdImage,
  MdOutlineBarChart,
  MdTaskAlt,
  MdExtension,
  MdWeb,
} from 'react-icons/md';
import { CgOrganisation } from 'react-icons/cg';
import { FaShop } from 'react-icons/fa6';
import styled from 'styled-components/macro';

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    height: 19px;
    width: 19px;
  }
`;

export const useHorizontalRoutes = () => {
  const { t, i18n } = useTranslation();

  const getLoginAs = useCallback(
    (onClick: () => void) => ({
      title: t('Login As'),
      shortTitle: t('Login As'),
      icon: (
        <IconContainer>
          <MdLaunch />
        </IconContainer>
      ),
      path: '#',
      isDisabled: false,
      onItemClick: onClick,
    }),
    [t]
  );

  const getDevAdminItems = useCallback(
    ({
      userId,
      customerId,
      superAdminRole,
      onLoginAsClick,
    }: {
      userId: string;
      customerId: string;
      superAdminRole: SuperAdminRole;
      onLoginAsClick: () => void;
    }) => {
      const {
        showCustomers,
        showResellers,
        showUsageReports,
        showUsers,
        showOrganizations,
        showIms,
        showMlm,
        showIntegrations,
        showLoginAs,
        showLandingPageBuilder,
      } = getAdminAccessList(superAdminRole);

      const showAssignments =
        CAMPING_WORLD_IDS.includes(userId.toString()) ||
        ALLOWED_CUSTOMERS.concat(ADMIN_CUSTOMER_IDS).includes(
          customerId.toString()
        );

      let innerMenu = [];
      if (showMlm) {
        innerMenu.push({
          title: t('Multi-Location Manager'),
          shortTitle: t('MLM'),
          icon: <MdLocationPin size={24} />,
          path: '/admin/multilocations',
          isDisabled: false,
        });
      }

      if (showIms) {
        innerMenu.push({
          title: t('IMS'),
          shortTitle: t('IMS'),
          icon: <MdAddLink size={24} />,
          path: '/admin/ims',
          isDisabled: false,
        });
      }

      if (showOrganizations) {
        innerMenu.push({
          title: t('Organizations'),
          shortTitle: t('Organizations'),
          icon: <CgOrganisation size={20} />,
          path: '/admin/organizations',
          isDisabled: false,
        });
      }

      if (showResellers) {
        innerMenu.push({
          title: t('Resellers'),
          shortTitle: t('Resellers'),
          icon: <FaShop size={20} />,
          path: '/admin/resellers',
          isDisabled: false,
        });
      }

      if (showCustomers) {
        const customersItem = {
          title: t('Manage Customers'),
          shortTitle: t('Customers'),
          icon: <MdContacts size={24} />,
          path: '/admin/customers',
          isDisabled: false,
        };
        innerMenu.push({
          ...customersItem,
          title:
            superAdminRole === SuperAdminRole.ADMIN
              ? t('Company Profile')
              : customersItem.title,
          path:
            superAdminRole === SuperAdminRole.ADMIN
              ? `/admin/customers/${customerId}/${SCREEN_VALUES.CUSTOMERS}/${DEFAULT_RESELLER_ID}`
              : customersItem.path,
        });
      }

      if (showUsers) {
        innerMenu.push({
          title: t('Manage Users'),
          shortTitle: t('Users'),
          icon: <MdSupervisorAccount size={20} />,
          path: '/admin/users',
          isDisabled: false,
        });
      }

      if (showAssignments) {
        innerMenu.push({
          title: t('Manage Assignments'),
          shortTitle: t('Assignments'),
          icon: <MdTaskAlt size={18} />,
          path: '/company/Company_ManageAssignedUsers',
          isDisabled: false,
        });
      }

      if (showUsageReports) {
        innerMenu.push({
          title: t('Usage Reports'),
          shortTitle: t('Usage Reports'),
          icon: <MdOutlineBarChart size={20} />,
          path: '/admin/usage-reports',
          isDisabled: false,
        });
      }

      innerMenu.push({
        title: t('Graphics'),
        shortTitle: t('Graphics'),
        icon: <MdImage size={20} />,
        path: '/company/Company_Graphics',
        isDisabled: false,
      });

      if (showIntegrations) {
        innerMenu.push({
          title: t('Integrations'),
          shortTitle: t('Integrations'),
          icon: <MdExtension size={20} />,
          path: '/admin/integrations',
          isDisabled: false,
        });
      }

      if (
        showLandingPageBuilder &&
        checkTemporaryShowLandingPageBuilderExceptions(userId, customerId)
      ) {
        innerMenu.push({
          title: t('Landing Page Builder'),
          shortTitle: t('LP Builder'),
          icon: <MdWeb size={20} />,
          path: '/admin/landing-pages-v2',
          isDisabled: false,
        });
      }

      if (showLoginAs) {
        innerMenu.push(getLoginAs(onLoginAsClick));
      }

      return {
        title: t('Admin'),
        path: '/admin/customers',
        innerMenu,
      };
    },
    [t, getLoginAs]
  );

  return useMemo(
    () => ({
      horizontalItemsAll: [
        {
          title: t('Library'),
          path: '/home',
          isDisabled: false,
        },
        {
          title: t('Reports'),
          path: '/reports/overview',
          isDisabled: false,
          productFeatureId: productFeature.REPORTS,
        },
        {
          title: t('Contacts'),
          path: '/contacts',
          isDisabled: false,
        },
        {
          title: t('Conversations'),
          path: '/sms/overview',
          isDisabled: false,
          productFeatureId: productFeature.CONVERSATIONS,
        },
        {
          title: t('Conversationsv2'),
          path: '/conversations',
          isDisabled: false,
          productFeatureId: productFeature.CONVERSATIONS,
        },
        {
          title: t('Integrations'),
          path: '/integrations',
          isDisabled: false,
        },
        {
          title: t('Design'),
          path: '/design/landing-pages',
          isDisabled: false,
        },
      ],
      horizontalItemsAllAssignment: [
        {
          title: t('Library'),
          path: '/home',
          isDisabled: false,
        },
        {
          title: t('Reports'),
          path: '/reports/overview',
          isDisabled: false,
          productFeatureId: productFeature.REPORTS,
        },
        {
          title: t('Contacts'),
          path: '/contacts',
          isDisabled: false,
        },
        {
          title: t('Conversations'),
          path: '/sms/overview',
          isDisabled: false,
          productFeatureId: productFeature.CONVERSATIONS,
        },
        {
          title: t('Conversationsv2'),
          path: '/conversations',
          isDisabled: false,
          productFeatureId: productFeature.CONVERSATIONS,
        },
        {
          title: t('Integrations'),
          path: '/integrations',
          isDisabled: false,
        },
        {
          title: t('Design'),
          path: '/design/landing-pages',
          isDisabled: false,
        },
        {
          title: t('Assignments'),
          path: '/company/Company_ManageAssignedUsers',
          isDisabled: false,
        },
      ],
      horizontalItemsSupervisor: [
        {
          title: t('Reports'),
          path: '/reports/overview',
          isDisabled: false,
          productFeatureId: productFeature.REPORTS,
        },
        {
          title: t('Conversations'),
          path: '/sms/overview',
          isDisabled: false,
          productFeatureId: productFeature.CONVERSATIONS,
        },
        {
          title: t('Design'),
          path: '/design/landing-pages',
          isDisabled: false,
        },
      ],
      horizontalItemsUser: [
        {
          title: t('Library'),
          path: '/home',
          isDisabled: false,
        },
        {
          title: t('Reports'),
          path: '/reports/overview',
          isDisabled: false,
          productFeatureId: productFeature.REPORTS,
        },
        {
          title: t('Contacts'),
          path: '/contacts',
          isDisabled: false,
        },
        {
          title: t('Conversations'),
          path: '/sms/overview',
          isDisabled: false,
          productFeatureId: productFeature.CONVERSATIONS,
        },
        {
          title: t('Conversationsv2'),
          path: '/conversations',
          isDisabled: false,
          productFeatureId: productFeature.CONVERSATIONS,
        },
        {
          title: t('Integrations'),
          path: '/integrations',
          isDisabled: false,
        },
        {
          title: t('Design'),
          path: '/design/landing-pages',
          isDisabled: false,
        },
      ],
      horizontalItemsAutomotive: [
        {
          title: t('Repair Orders'),
          path: '/repair-orders',
          isDisabled: false,
        },
      ],
      horizontalItemsAutomotiveSales: [
        {
          title: t('Inventory'),
          path: '/inventory',
          isDisabled: false,
        },
      ],
      // Admin navigation items
      mlm: {
        title: t('Multi-Location Manager'),
        shortTitle: t('MLM'),
        icon: <MdLocationPin size={24} />,
        path: '/admin/multilocations',
        isDisabled: false,
      },
      ims: {
        title: t('IMS'),
        shortTitle: t('IMS'),
        icon: <MdAddLink size={24} />,
        path: '/admin/ims',
        isDisabled: false,
      },
      organization: {
        title: t('Organizations'),
        shortTitle: t('Organizations'),
        icon: <CgOrganisation size={20} />,
        path: '/admin/organizations',
        isDisabled: false,
      },
      reseller: {
        title: t('Resellers'),
        shortTitle: t('Resellers'),
        icon: <FaShop size={20} />,
        path: '/admin/resellers',
        isDisabled: false,
      },
      customers: {
        title: t('Manage Customers'),
        shortTitle: t('Customers'),
        icon: <MdContacts size={24} />,
        path: '/admin/customers',
        isDisabled: false,
      },
      users: {
        title: t('Manage Users'),
        shortTitle: t('Users'),
        icon: <MdSupervisorAccount size={20} />,
        path: '/admin/users',
        isDisabled: false,
      },
      assignments: {
        title: t('Manage Assignments'),
        shortTitle: t('Assignments'),
        icon: <MdTaskAlt size={18} />,
        path: '/company/Company_ManageAssignedUsers',
        isDisabled: false,
      },
      usageReport: {
        title: t('Usage Reports'),
        shortTitle: t('Usage Reports'),
        icon: <MdOutlineBarChart size={20} />,
        path: '/admin/usage-reports',
        isDisabled: false,
      },
      graphics: {
        title: t('Graphics'),
        shortTitle: t('Graphics'),
        icon: <MdImage size={20} />,
        path: '/company/Company_Graphics',
        isDisabled: false,
      },
      integrations: {
        title: t('Integrations'),
        shortTitle: t('Integrations'),
        icon: <MdExtension size={20} />,
        path: '/admin/integrations',
        isDisabled: false,
      },
      landingPageBuilder: {
        title: t('Landing Page Builder'),
        shortTitle: t('LP Builder'),
        icon: <MdWeb size={20} />,
        path: '/admin/landing-pages-v2',
        isDisabled: false,
      },
      getLoginAs,
      getDevAdminItems,
    }),
    [i18n.language]
  );
};
